import { localStorageGet, localStorageSet } from '~/app/helpers/localStorage';
import store from '~/store';
import { syncQueue } from '~/data/sync/SyncQueue';

const getUserId = (): string => {
  const user = store?.getState()?.user?.data;
  return user?.id ? String(user.id) : 'default';
};

const getFavoriteMachinesKey = (userId?: string) =>
  `offline-favorite-machine-ids-user-${userId || getUserId()}`;

const getFavoriteProjectsKey = (userId?: string) =>
  `offline-favorite-project-ids-user-${userId || getUserId()}`;

/** Replace-only snapshot of the last successful GET /machines/last-used (server truth for offline guards). */
const getLastUsedMachinesKey = (userId?: string) =>
  `offline-last-used-machines-user-${userId || getUserId()}`;

/** Replace-only snapshot of the last successful GET /projects/last-used. */
const getLastUsedProjectsKey = (userId?: string) =>
  `offline-last-used-projects-user-${userId || getUserId()}`;

const readIdList = (key: string): string[] => {
  const raw = localStorageGet(key);
  if (raw === null || raw === undefined) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    // Tolerate legacy [{ id, usedAt }] snapshots until the next server refresh replaces them.
    return parsed
      .map((item) => {
        if (item !== null && typeof item === 'object' && 'id' in item) {
          return String((item as { id: unknown }).id);
        }
        return String(item);
      })
      .filter((id) => id && id !== 'undefined' && id !== 'null');
  } catch (e) {
    return [];
  }
};

export const setFavoriteMachineIds = (
  ids: Array<string | number>,
  userId?: string
): void => {
  localStorageSet(
    getFavoriteMachinesKey(userId),
    JSON.stringify(ids.map((id) => String(id)))
  );
};

export const getFavoriteMachineIds = (userId?: string): string[] =>
  readIdList(getFavoriteMachinesKey(userId));

export const setFavoriteProjectIds = (
  ids: Array<string | number>,
  userId?: string
): void => {
  localStorageSet(
    getFavoriteProjectsKey(userId),
    JSON.stringify(ids.map((id) => String(id)))
  );
};

export const getFavoriteProjectIds = (userId?: string): string[] =>
  readIdList(getFavoriteProjectsKey(userId));

/**
 * Replace the offline last-used machine snapshot with the server GET /last-used result.
 * Do not merge with any local usage history.
 */
export const setLastUsedMachineIds = (
  ids: Array<string | number>,
  userId?: string
): void => {
  localStorageSet(
    getLastUsedMachinesKey(userId),
    JSON.stringify(ids.map((id) => String(id)))
  );
};

export const getLastUsedMachineIds = (userId?: string): string[] =>
  readIdList(getLastUsedMachinesKey(userId));

/**
 * Replace the offline last-used project snapshot with the server GET /last-used result.
 */
export const setLastUsedProjectIds = (
  ids: Array<string | number>,
  userId?: string
): void => {
  localStorageSet(
    getLastUsedProjectsKey(userId),
    JSON.stringify(ids.map((id) => String(id)))
  );
};

export const getLastUsedProjectIds = (userId?: string): string[] =>
  readIdList(getLastUsedProjectsKey(userId));

const isFavoriteMachineId = (
  id: string | number,
  userId?: string
): boolean => {
  const favoriteIds = getFavoriteMachineIds(userId);
  return favoriteIds.includes(String(id));
};

export const isLastUsedMachine = (
  id: string | number,
  userId?: string
): boolean => {
  return getLastUsedMachineIds(userId).includes(String(id));
};

const isFavoriteProjectId = (
  id: string | number,
  userId?: string
): boolean => {
  const favoriteIds = getFavoriteProjectIds(userId);
  return favoriteIds.includes(String(id));
};

const isLastUsedProject = (
  id: string | number,
  userId?: string
): boolean => {
  return getLastUsedProjectIds(userId).includes(String(id));
};

/**
 * Access control for machines offline.
 * Supports ALL favorites and ALL server last-used machines without limits.
 */
export const canAccessMachineOffline = (
  id: string | number,
  inMemoryFavorite = false,
  userId?: string
): boolean =>
  inMemoryFavorite || isFavoriteMachineId(id, userId) || isLastUsedMachine(id, userId);

/**
 * Access control for projects offline.
 * Supports ALL favorites and ALL server last-used projects without limits.
 */
export const canAccessProjectOffline = (
  id: string | number,
  inMemoryFavorite = false,
  userId?: string
): boolean =>
  inMemoryFavorite || isFavoriteProjectId(id, userId) || isLastUsedProject(id, userId);

/**
 * Check if a machine has pending sync queue entries.
 * Used to protect against evicting cached data when queued sync operations exist.
 */
export const hasPendingSyncForMachine = async (
  machineId: string | number,
  userId?: string
): Promise<boolean> => {
  const uid = userId || getUserId();
  const rows = await syncQueue.listByUserId(Number(uid));
  const strId = String(machineId);
  return rows.some((row) => {
    try {
      const data = JSON.parse(row.data);
      return String(data?.machineId) === strId;
    } catch {
      return false;
    }
  });
};

/**
 * Check if a project has pending sync queue entries.
 */
export const hasPendingSyncForProject = async (
  projectId: string | number,
  userId?: string
): Promise<boolean> => {
  const uid = userId || getUserId();
  const rows = await syncQueue.listByUserId(Number(uid));
  const strId = String(projectId);
  return rows.some((row) => {
    try {
      const data = JSON.parse(row.data);
      return String(data?.projectId) === strId;
    } catch {
      return false;
    }
  });
};
